import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import { ICollection, IDtoResponse } from '@shared/models/dtos/response.dto';
import {
  ICashDisposition,
  IContract,
  IContractBalance,
  IContractCancelReason,
  IContractStatementInfoResponse,
  IContractStatementParams,
  IContractStatementQuery,
  ICardFreezeUnfreezeReason,
  ICardReasonsParams,
  IContractStatementResponse,
  ISharedCards,
} from '@app/shared/models/contract.model';
import { APP_CONFIG } from '@app/shared/config/app.config';
import { ServiceBase } from '../base/base.service';
import {
  IBlockUnblockContract,
  IBlockUnlockTransaction,
  ICancelAccountRequest,
  ICancelAccountTransaction,
  ICardFeaturesUpdate,
  IFreezeUnfreezeCardRequest,
  IReplaceCardContract,
} from '@app/shared/models/banking.model';
import { AppContext } from '../context/app.context.service';
import { CONTRACT_STATE, CONTRACT_TYPE, COUNTRY, PARAM_BLOCK_REASON } from '@app/shared/types/enums';
import { ICertificateResponse } from '@app/shared/models/certificate-response.model';
import { BlockReasonParam } from '@app/shared/models/param.model';
import { response } from 'msw';
import { PATH } from '@app/shared/types/enums/path';
import { ActionEnum } from '@app/shared/types/enums/ActionEnum.enum';
import { channel } from 'version';
@Injectable({
  providedIn: 'root',
})
export class CardBasicService extends ServiceBase {
  constructor(private readonly http: HttpClient, private readonly _appCtx: AppContext) {
    super(`${APP_CONFIG.serviceBaseUrl}/wallet/api/v1/card-basic`);
  }

  getContracts(rppUserId: string): Observable<IContract[] | undefined> {
    const headers = new HttpHeaders().set('monitoring_view', PATH.CREDIT).set('monitoring_action', ActionEnum.READ);

    return this.http
      .get<IDtoResponse<IContract[]>>(`${this.baseUrl}/${rppUserId}/contracts`, {
        headers,
      })
      .pipe(
        map(response => {
          response.data?.sort((a, b) => {
            if (a.state === CONTRACT_STATE.ACTIVE && b.state !== CONTRACT_STATE.ACTIVE) {
              return -1;
            } else if (a.state !== CONTRACT_STATE.ACTIVE && b.state === CONTRACT_STATE.ACTIVE) {
              return 1;
            } else {
              return 0;
            }
          });

          return response.data;
        })
      );
  }

  getSharedCards(rppUserId: string, contractCode: string): Observable<ISharedCards | undefined> {
    const headers = new HttpHeaders()
      .set('monitoring_view', PATH.CREDIT)
      .set('monitoring_traceability_id', this._appCtx.getTraceSessionId())
      .set('monitoring_action', ActionEnum.READ)
      .set('channel', channel + '-' + this._appCtx.getUserType());

    return this.http
      .get<IDtoResponse<ISharedCards>>(`${this.baseUrl}/${rppUserId}/contract/${contractCode}/additional-cards`, {
        headers,
      })
      .pipe(map(response => response.data));
  }

  /**
   * this method is used to get the contracts of type credit to update the view
   * @param rppUserId
   * @returns Observable<IContract[]>
   */

  getFilteredContracts(rppUserId: string): Observable<IContract[]> {
    return this.getContracts(rppUserId).pipe(map(_c => _c?.filter(c => c.type === CONTRACT_TYPE.CREDIT) || []));
  }

  /**
   * @deprecated use getBalance instead
   *
   *
   */
  getBalances(rppUserId: string): Observable<ICollection<IContractBalance> | undefined> {
    return this.http
      .get<IDtoResponse<ICollection<IContractBalance>>>(`${this.baseUrl}/${rppUserId}/balances`)
      .pipe(map(response => response.data));
  }

  getBalance(rppUserId: string, contractCode: string): Observable<IContractBalance | undefined> {
    const headers = new HttpHeaders()
      .set('monitoring_view', PATH.CREDIT)
      .set('monitoring_traceability_id', this._appCtx.getTraceSessionId())
      .set('monitoring_action', ActionEnum.READ)
      .set('channel', channel + '-' + this._appCtx.getUserType());

    return this.http
      .get<IDtoResponse<IContractBalance>>(`${this.baseUrl}/${rppUserId}/contract/${contractCode}/balance`, {
        headers,
      })
      .pipe(map(response => response.data));
  }

  getLimitIncrements(rppUserId: string, contractCode: string): Observable<any> {
    const headers = new HttpHeaders()
      .set('monitoring_view', PATH.CREDIT)
      .set('monitoring_traceability_id', this._appCtx.getTraceSessionId())
      .set('monitoring_action', ActionEnum.READ)
      .set('channel', channel + '-' + this._appCtx.getUserType());

    return this.http
      .get<any>(`${this.baseUrl}/${rppUserId}/contract/${contractCode}/limit-increments`, {
        headers,
      })
      .pipe(map(response => response.data));
  }

  getCashWithdrawalAmount(rppUserId: string): Observable<ICashDisposition | undefined> {
    const headers = new HttpHeaders()
      .set('monitoring_view', PATH.CREDIT)
      .set('monitoring_traceability_id', this._appCtx.getTraceSessionId())
      .set('monitoring_action', ActionEnum.READ)
      .set('channel', channel + '-' + this._appCtx.getUserType());

    return this.http
      .get<IDtoResponse<ICashDisposition>>(`${this.baseUrl}/${rppUserId}/contracts/cash-withdrawal`, {
        headers,
      })
      .pipe(map(response => response.data));
  }

  getCancelReasons(contractType: string): Observable<IContractCancelReason[] | undefined> {
    const headers = new HttpHeaders().set('monitoring_view', PATH.CREDIT).set('monitoring_action', ActionEnum.READ);

    return this.http
      .get<IDtoResponse<IContractCancelReason[]>>(`${this.baseUrl}/contract-cancellation-reasons/${contractType}`, {
        headers,
      })
      .pipe(map(response => response.data));
  }

  getFreezeUnfreezeReasons(toogleFreeze: string): Observable<ICardFreezeUnfreezeReason[] | undefined> {
    const headers = new HttpHeaders().set('monitoring_view', PATH.CREDIT).set('monitoring_action', ActionEnum.READ);

    return this.http
      .get<IDtoResponse<ICardFreezeUnfreezeReason[]>>(`${this.baseUrl}/freeze-unfreeze-card-reasons/${toogleFreeze}`, {
        headers,
      })
      .pipe(map(response => response.data));
  }

  cancelAction(rppUserId: string, contractCode: string, body: ICancelAccountRequest): Observable<ICancelAccountTransaction | undefined> {
    const headers = new HttpHeaders().set('monitoring_view', PATH.CREDIT).set('monitoring_action', ActionEnum.UPDATE);

    return this.http
      .post<IDtoResponse<ICancelAccountTransaction>>(`${this.baseUrl}/${rppUserId}/card/${contractCode}/cancel`, { ...body }, { headers })
      .pipe(map(response => response.data));
  }

  blockAction(blockUnblockParams: IBlockUnblockContract): Observable<IBlockUnlockTransaction | undefined> {
    const { rpp_user_id, contract_code, type, reason, email_support } = blockUnblockParams;
    const url = `${this.baseUrl}/${rpp_user_id}/contract/${contract_code}/${type}`;

    const body = {
      code: reason.code,
      description: reason.description,
    };

    const headers = new HttpHeaders()
      .set('agent_email', email_support.toString())
      .set('monitoring_view', PATH.CREDIT)
      .set('monitoring_action', ActionEnum.UPDATE)
      .set('transaction_origin', 'BY_CUSTOMER_SERVICE');

    return this.http.post<IDtoResponse<IBlockUnlockTransaction>>(url, body, { headers }).pipe(map(response => response.data));
  }

  getBlockUnblockReasons(params: ICardReasonsParams): Observable<IContractCancelReason[] | undefined> {
    const httpParams = Object.entries(params).reduce((acc, [key, value]) => {
      return acc.set(key, value);
    }, new HttpParams());
    const headers = new HttpHeaders()
      .set('monitoring_view', PATH.CREDIT)
      .set('monitoring_traceability_id', this._appCtx.getTraceSessionId())
      .set('monitoring_action', ActionEnum.READ)
      .set('channel', channel + '-' + this._appCtx.getUserType());

    return this.http
      .get<IDtoResponse<IContractCancelReason[]>>(`${this.baseUrl}/card-basic-reasons`, { params: httpParams, headers })
      .pipe(map(response => response.data));
  }

  getBlockUnblockReasonsScope(params: BlockReasonParam): Observable<IContractCancelReason[] | undefined> {
    const headers = new HttpHeaders()
      .set('monitoring_view', PATH.CREDIT)
      .set('monitoring_traceability_id', this._appCtx.getTraceSessionId())
      .set('monitoring_action', ActionEnum.READ)
      .set('channel', channel + '-' + this._appCtx.getUserType());

    return this.http
      .get<IDtoResponse<IContractCancelReason[]>>(`${APP_CONFIG.serviceBaseUrl}/wallet/api/v1/blocking/scope/rules`, {
        headers,
        params: new HttpParams()
          .set(PARAM_BLOCK_REASON.SCOPE, params.scope)
          .set(PARAM_BLOCK_REASON.CONTRACT_TYPE, params.contractType)
          .set(PARAM_BLOCK_REASON.PRODUCT_TYPE, params.productType)
          .set(PARAM_BLOCK_REASON.REASON_TYPE, params.reasonType),
      })
      .pipe(map(response => response.data));
  }

  /**
   *
   * @deprecated It uses the new v2
   */
  getStatementPdf(params: IContractStatementQuery): Observable<any> {
    const { rppUserId, contractCode, month, year } = params;

    return this.http.get(`${this.baseUrl}/${rppUserId}/contract/${contractCode}/statement-pdf`, {
      params: {
        month,
        year,
      },
      responseType: 'arraybuffer',
    });
  }

  getStatementPdfV2(params: IContractStatementQuery): Observable<IContractStatementResponse | undefined> {
    const { rppUserId, contractCode, month, year } = params;
    const headers = new HttpHeaders()
      .set('monitoring_view', PATH.CREDIT)
      .set('monitoring_traceability_id', this._appCtx.getTraceSessionId())
      .set('monitoring_action', ActionEnum.READ)
      .set('channel', channel + '-' + this._appCtx.getUserType());

    return this.http
      .get<IDtoResponse<IContractStatementResponse>>(`${this.baseUrl}/${rppUserId}/contract/${contractCode}/v2/statement-pdf`, {
        headers,
        params: {
          month,
          year,
        },
      })
      .pipe(map(res => res.data));
  }

  /**
   *
   * @deprecated It uses the new v2
   */
  getStatementInfo(params: IContractStatementParams): Observable<IContractStatementInfoResponse | undefined> {
    const { rppUserId, contractCode } = params;

    return this.http
      .get<IDtoResponse<IContractStatementInfoResponse>>(`${this.baseUrl}/${rppUserId}/contract/${contractCode}/statement-info`)
      .pipe(map(res => res.data));
  }

  freezeUnfreezeCard(rppUserId: string, cardCode: string, body: IFreezeUnfreezeCardRequest): Observable<IDtoResponse<any>> {
    const headers = new HttpHeaders()
      .set('monitoring_view', PATH.CREDIT)
      .set('monitoring_traceability_id', this._appCtx.getTraceSessionId())
      .set('monitoring_action', ActionEnum.UPDATE)
      .set('channel', channel + '-' + this._appCtx.getUserType());

    return this.http.patch<IDtoResponse<any>>(`${this.baseUrl}/${rppUserId}/card/${cardCode}/freeze-unfreeze`, { ...body, headers });
  }

  replaceCard(replaceCardParams: IReplaceCardContract): Observable<IBlockUnlockTransaction | undefined> {
    const { rpp_user_id, card_code, reason, agent_email } = replaceCardParams;

    const body = {
      code: reason.code,
      description: reason.description,
    };

    const headers = new HttpHeaders()
      .set('monitoring_view', PATH.CREDIT)
      .set('monitoring_traceability_id', this._appCtx.getTraceSessionId())
      .set('monitoring_action', ActionEnum.UPDATE)
      .set('channel', channel + '-' + this._appCtx.getUserType())
      .set('agent_email', agent_email.toString());

    return this.http
      .put<IDtoResponse<IBlockUnlockTransaction>>(
        `${this.baseUrl}/${rpp_user_id}/card/${card_code}/replace`,
        { ...body },
        {
          headers,
        }
      )
      .pipe(map(response => response.data));
  }

  replaceDebitCard(replaceCardParams: IReplaceCardContract): Observable<IBlockUnlockTransaction | undefined> {
    const { rpp_user_id, card_code, reason, agent_email } = replaceCardParams;

    const body = {
      code: reason.code,
      description: reason.description,
    };

    const headers = new HttpHeaders()
      .set('monitoring_view', PATH.ACCOUNT)
      .set('monitoring_traceability_id', this._appCtx.getTraceSessionId())
      .set('monitoring_action', ActionEnum.UPDATE)
      .set('channel', channel + '-' + this._appCtx.getUserType())
      .set('agent_email', agent_email.toString());

    return this.http
      .put<IDtoResponse<IBlockUnlockTransaction>>(
        `${this.baseUrl}/${rpp_user_id}/card/${card_code}/replace-debit`,
        { ...body },
        { headers }
      )
      .pipe(map(response => response.data));
  }

  getCardSettings(rppUserId: string, contractCode: string) {
    const headers = new HttpHeaders()
      .set('monitoring_view', PATH.CREDIT)
      .set('monitoring_traceability_id', this._appCtx.getTraceSessionId())
      .set('monitoring_action', ActionEnum.READ)
      .set('channel', channel + '-' + this._appCtx.getUserType());

    return this.http
      .get<IDtoResponse<any>>(`${this.baseUrl}/${rppUserId}/contract/${contractCode}/card-features`, {
        headers,
      })
      .pipe(map(res => res.data));
  }

  updateCardFeatures(params: any) {
    const { rppUserId, contractCode, features } = params;
    const headers = new HttpHeaders()
      .set('monitoring_view', PATH.CREDIT)
      .set('monitoring_traceability_id', this._appCtx.getTraceSessionId())
      .set('monitoring_action', ActionEnum.UPDATE)
      .set('channel', channel + '-' + this._appCtx.getUserType());

    return this.http
      .patch<IDtoResponse<any>>(`${this.baseUrl}/${rppUserId}/contract/${contractCode}/card-features`, {
        features,
        headers,
      })
      .pipe(
        map(res => {
          return res.data;
        })
      );
  }

  getStatementInfoV2(params: IContractStatementParams): Observable<string[] | undefined> {
    const { rppUserId, contractCode } = params;
    const headers = new HttpHeaders()
      .set('monitoring_view', PATH.CREDIT)
      .set('monitoring_traceability_id', this._appCtx.getTraceSessionId())
      .set('monitoring_action', ActionEnum.READ)
      .set('channel', channel + '-' + this._appCtx.getUserType());

    return this.http
      .get<IDtoResponse<string[]>>(`${this.baseUrl}/${rppUserId}/contract/${contractCode}/v2/statement-info`, {
        headers,
      })
      .pipe(map(res => res.data));
  }

  migrationCertificateDD(
    rppUserId: string,
    contractCode: string,
    certificate: string,
    year?: string,
    typeCertificate?: string,
    contractCodeHistory?: string
  ): Observable<ICertificateResponse | undefined> {
    const url = year
      ? `${this.baseUrl}/${rppUserId}/contract/${contractCode}/v2/tax-certificate/download?year=${year}&productType=${typeCertificate}&contractCode=${contractCodeHistory}`
      : `${this.baseUrl}/${rppUserId}/contract/${contractCode}/CREDIT/document/${certificate}`;
    const headers = new HttpHeaders().set('monitoring_view', PATH.CREDIT).set('monitoring_action', ActionEnum.READ);

    return this.http.get<IDtoResponse<ICertificateResponse>>(`${url}`, { headers }).pipe(map(res => res.data));
  }

  getTaxCertificateAvailable(rppUserId: string, contractCode: string) {
    const headers = new HttpHeaders().set('monitoring_view', PATH.CREDIT).set('monitoring_action', ActionEnum.READ);

    return this.http
      .get<IDtoResponse<any>>(`${this.baseUrl}/${rppUserId}/contract/${contractCode}/v2/tax-certificate/available`, {
        headers,
      })
      .pipe(map(res => res.data));
  }

  getRactYears(rppUserId: string, contractCode: string): Observable<number[]> {
    const headers = new HttpHeaders().set('monitoring_view', PATH.CREDIT).set('monitoring_action', ActionEnum.READ);

    return this.http
      .get<IDtoResponse<{ years: number[] }>>(`${this.baseUrl}/${rppUserId}/contract/${contractCode}/ract/available`, { headers })
      .pipe(map(res => res.data?.years || []));
  }

  downloadRactCertificate(rppUserId: string, contractCode: string, year: number, email: string): Observable<ICertificateResponse | undefined> {
    const headers = new HttpHeaders().set('monitoring_view', PATH.CREDIT).set('monitoring_action', ActionEnum.READ);

    return this.http
      .get<IDtoResponse<ICertificateResponse>>(
        `${this.baseUrl}/${rppUserId}/contract/${contractCode}/ract/download?year=${year}&email=${encodeURIComponent(email)}`,
        { headers }
      )
      .pipe(map(res => res.data));
  }
}
